/**
 * Generic New Relic page action tracker with isolated agent support
 *
 * Uses a global registry to share the isolated agent across different bundles.
 * This ensures that wallet-manager (parent page bundle) and web components
 * (shadow DOM bundle) can both access the same isolated agent instance.
 *
 * Agent selection logic:
 * - If an isolated agent has been set via global registry: uses that agent
 * - Otherwise: falls back to window.newrelic (for regular page code)
 */

// Global registry key for sharing agent across bundles
const GLOBAL_AGENT_KEY = '__atc_isolated_newrelic_agent__';
const AGENT_READY_KEY = '__atc_isolated_agent_ready__';
const PENDING_ACTIONS_KEY = '__atc_pending_page_actions__';

/**
 * Gets the isolated New Relic agent from the global registry
 * @returns {object|null} The isolated agent or null
 */
const getIsolatedAgent = () => typeof window !== 'undefined' && window[GLOBAL_AGENT_KEY] || null;

/**
 * Checks if the isolated agent is ready
 * @returns {boolean}
 */
const isAgentReady = () => typeof window !== 'undefined' && window[AGENT_READY_KEY] || false;

/**
 * Gets the pending actions queue
 * @returns {Array}
 */
const getPendingActions = () => {
  if (typeof window === 'undefined') return [];
  if (!window[PENDING_ACTIONS_KEY]) {
    window[PENDING_ACTIONS_KEY] = [];
  }
  return window[PENDING_ACTIONS_KEY];
};

/**
 * Processes all pending page actions
 */
const processPendingActions = () => {
  const agent = getIsolatedAgent();
  if (!agent?.addPageAction) return;
  const pending = getPendingActions();
  while (pending.length > 0) {
    const {
      actionName,
      attributes
    } = pending.shift();
    agent.addPageAction(actionName, attributes);
  }
};

/**
 * Sets the isolated New Relic agent in the global registry so it can be
 * shared across all bundles (web components, wallet-manager, etc.)
 * @param {object} agent - The isolated New Relic browser agent instance
 */
const setIsolatedNewRelicAgent = agent => {
  if (typeof window !== 'undefined') {
    window[GLOBAL_AGENT_KEY] = agent;
    window[AGENT_READY_KEY] = true;
    // Process any actions that were queued before agent was ready
    processPendingActions();
  }
};

/**
 * Tracks a New Relic page action with custom attributes.
 * Uses isolated agent from global registry if available, otherwise falls back to window.newrelic.
 * If isolated agent is expected but not ready yet, queues the action.
 * @param {string} actionName
 * @param {object} attributes
 */
const trackPageAction = function (actionName) {
  let attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const isolatedAgent = getIsolatedAgent();

  // Queue if in web component context and isolated agent not ready yet
  if (typeof window !== 'undefined' && typeof document !== 'undefined' && document.querySelector('unified-deal-starter') && !isAgentReady()) {
    getPendingActions().push({
      actionName,
      attributes
    });
    return true;
  }
  const newrelicAgent = isolatedAgent || window && window.newrelic;
  if (newrelicAgent?.addPageAction) {
    newrelicAgent.addPageAction(actionName, attributes);
  }
  return true;
};
export { trackPageAction as default, setIsolatedNewRelicAgent };
